/**
 * Esquemas de validacao compartilhados entre API e web.
 *
 * Uma unica definicao evita divergencia entre o que o formulario aceita e o que
 * o servidor valida. O servidor SEMPRE revalida — o uso na web e conveniencia
 * de UX, nunca controle.
 */

import { z } from 'zod';
import { FASES_DO_NEGOCIO } from './fases-do-negocio.js';
import {
  COMMERCIAL_CLASSIFICATION,
  ACCOUNT_SIZE_TIER,
  ACCOUNT_STATUS,
  ACTIVITY_DIRECTION,
  ACTIVITY_PRIORITY,
  ACTIVITY_STATUS,
  ACTIVITY_TYPE,
  BRAZILIAN_STATES,
  CONFLICT_RESOLUTION_TYPE,
  CONSENT_STATUS,
  CONTACT_ROLE_TYPE,
  CONTACT_STATUS,
  CONTRIBUTION_TYPE,
  CATALOG_BILLING_PERIODICITY,
  CATALOG_ITEM_KIND,
  CATALOG_PRICING_MODEL,
  COMMERCIAL_APPROVAL_DECISION,
  OPPORTUNITY_TYPE,
  OPPORTUNITY_VALUE_SOURCE,
  BRIEFING_CONTENT_ORIGIN,
  PROPOSAL_FOLLOWUP_CHANNEL,
  PROPOSAL_NEXT_ACTION_TYPE,
  PROPOSAL_REJECTION_REASON,
  PROPOSAL_BLOCK_KIND,
  PROPOSAL_BLOCK_PRESENTATION,
  PROPOSAL_SEND_LIMITES,
  PROPOSAL_BLOCK_SOURCE,
  PROPOSAL_BLOCK_STYLE,
  PROPOSAL_COVER_ALIGNMENT,
  PROPOSAL_COVER_LAYOUT,
  PROPOSAL_IMAGE_ALIGNMENT,
  PROPOSAL_IMAGE_FIT,
  PROPOSAL_IMAGE_WIDTH,
  PROPOSAL_LOGO_SIZE,
  PROPOSAL_PAGE_NUMBER_FORMAT,
  PROPOSAL_TABLE_BORDER,
  PROPOSAL_TABLE_PADDING,
  PROPOSAL_TITLE_STYLE,
  PROPOSAL_TYPE_SCALE,
  PROPOSAL_TABLE_ALIGNMENT,
  PROPOSAL_TABLE_LIMITES,
  PROPOSAL_OUTCOME,
  PROPOSAL_TEMPLATE_FONT,
  PROPOSAL_TEMPLATE_PURPOSE,
  PROPOSAL_PURPOSE,
  PROPOSAL_CONTENT_PACKAGE,
  PROPOSAL_VISUAL_STYLE,
  PROPOSAL_VISUAL_TRAIT,
  PROPOSAL_TEMPLATE_SCOPE,
  PROPOSAL_SEND_CHANNEL,
  PARTNER_KIND,
  PARTNER_MODALITY,
  PARTNER_STATUS,
  RESERVATION_SCOPE_TYPE,
  STAGE_TYPE,
  USER_STATUS,
} from './enums.js';

/**
 * NOTA SOBRE `.strict()` (BKC-007)
 *
 * Todo schema de ESCRITA e estrito: chave desconhecida faz a requisicao ser
 * recusada com 422, em vez de ser descartada em silencio.
 *
 * O motivo e concreto. Zod, por padrao, REMOVE o que nao esta declarado. Um
 * cliente que enviasse `PATCH /partners/{id}` com campos de politica de reserva
 * recebia 200 e nenhum efeito — a resposta afirmava sucesso e nada havia sido
 * aplicado. Foi assim que BKC-017 apareceu nos testes: nao era defeito do
 * `updatePartner`, que aplica corretamente o que conhece, mas do contrato que
 * engolia o resto.
 *
 * Um 2xx precisa significar "o que voce pediu foi feito". Quando parte do pedido
 * e impossivel, a resposta correta e recusar, nao obedecer pela metade.
 *
 * Em schemas com `.refine()`, `.strict()` vem ANTES: `.refine()` devolve
 * `ZodEffects`, que nao expoe `.strict()`.
 */

// ==================== primitivos ============================================

export const uuidSchema = z.string().uuid('Identificador inválido.');

/** Texto obrigatorio, com aparas e limite. Rejeita string só de espaços. */
export const requiredText = (max: number, field = 'Campo') =>
  z
    .string()
    .trim()
    .min(1, `${field} é obrigatório.`)
    .max(max, `${field} deve ter no máximo ${max} caracteres.`);

/**
 * Texto opcional.
 *
 * Contrato de tres estados, essencial para PATCH parcial:
 *   - campo AUSENTE  -> `undefined` (não alterar)
 *   - string vazia   -> `null`      (limpar o campo)
 *   - texto          -> o texto aparado
 *
 * Converter ausente em `null` faria um PATCH apagar tudo que não foi enviado.
 */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish();

export const emailSchema = z.string().trim().toLowerCase().email('E-mail inválido.').max(320);

export const optionalEmailSchema = z
  .union([z.literal(''), emailSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

/**
 * CNPJ: aceita formatado, normaliza para 14 posicoes e valida o digito
 * verificador. Aceita o formato historico (14 digitos) e o ALFANUMERICO da
 * IN RFB 2.229/2024 (12 posicoes [0-9A-Z] + 2 digitos verificadores).
 */
export const cnpjSchema = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase().replace(/[^0-9A-Z]/g, ''))
  .refine((v) => v.length === 14, 'CNPJ deve ter 14 posições.')
  .refine(isValidCnpjDigits, 'CNPJ inválido: dígito verificador não confere.');

export const optionalCnpjSchema = z
  .union([z.literal(''), cnpjSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

/**
 * CPF, para o parceiro PESSOA (CRM-VAL-003 §21).
 *
 * Sequência repetida é recusada: `111.111.111-11` passa no cálculo do dígito e
 * não é CPF de ninguém — é o que se digita para preencher o campo. Aceitá-lo
 * levaria um documento inválido ao contrato gerado e à base de pagamento.
 */
export const cpfSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length === 11, 'CPF deve ter 11 dígitos.')
  .refine(isValidCpfDigits, 'CPF inválido: dígito verificador não confere.');

export function isValidCpfDigits(cpf: string): boolean {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  const digitos = cpf.split('').map(Number);
  const verificador = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += (digitos[i] ?? 0) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return verificador(9) === digitos[9] && verificador(10) === digitos[10];
}

/**
 * Documento do parceiro: CPF ou CNPJ, conforme o tipo.
 *
 * Um único campo em vez de dois porque a pessoa preenche UM documento — e dois
 * campos, um sempre vazio, empurram para o erro de digitar CNPJ no de CPF.
 */
export const optionalPartnerDocumentSchema = z
  .union([z.literal(''), cpfSchema, cnpjSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

/**
 * Validacao do digito verificador do CNPJ.
 * Duplicada em `apps/api/src/domain/cnpj.ts` para manter o dominio puro sem
 * depender de pacote externo; ambas sao cobertas por teste unitario.
 */
export function isValidCnpjDigits(cnpj: string): boolean {
  // Formato historico (14 digitos) OU alfanumerico (IN RFB 2.229/2024): as 12
  // primeiras posicoes aceitam letras; os dois verificadores sao numericos e o
  // calculo usa `codigo ASCII - 48` de cada caractere (digitos 0-9; letras
  // A-Z valem 17-42). Para CNPJ so de digitos o resultado e identico ao
  // historico.
  if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj) || /^(.)\1{13}$/.test(cnpj)) return false;
  const values = cnpj.split('').map((c) => c.charCodeAt(0) - 48);
  const check = (weights: number[]): number => {
    const sum = weights.reduce((acc, w, i) => acc + w * (values[i] ?? 0), 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return (
    check([5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cnpj[12]) &&
    check([6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cnpj[13])
  );
}

export const stateSchema = z.enum(BRAZILIAN_STATES);
export const optionalStateSchema = z
  .union([z.literal(''), stateSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

/** Dinheiro como string decimal (ADR-010): nunca aritmetica monetaria em `number`. */
export const moneySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim().replace(',', '.')))
  .refine((v) => /^\d{1,12}(\.\d{1,2})?$/.test(v), 'Valor monetário inválido.')
  .refine((v) => Number(v) >= 0, 'Valor não pode ser negativo.');

export const optionalMoneySchema = z
  .union([z.literal(''), moneySchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

export const percentageSchema = z.coerce.number().min(0).max(100);

/**
 * Percentual como STRING decimal, com no maximo duas casas. (BKC-022)
 *
 * String e nao numero pelo mesmo motivo do dinheiro (ADR-010): ponto flutuante
 * nao representa `33.33` exatamente, e uma divisao de resultado que erra na
 * terceira casa vira disputa de comissao. A validacao aceita `0` como entrada
 * malformada de propriedade? Nao: quem nao participa do resultado nao entra na
 * lista, entao o minimo e maior que zero.
 */
export const percentageStringSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}([.,]\d{1,2})?$/, 'Informe o percentual com até duas casas decimais.')
  .transform((valor) => valor.replace(',', '.'))
  .refine((valor) => Number(valor) > 0 && Number(valor) <= 100, {
    message: 'O percentual precisa ser maior que 0 e no máximo 100.',
  });

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato AAAA-MM-DD.');

export const optionalIsoDateSchema = z
  .union([z.literal(''), isoDateSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const optionalIsoDateTimeSchema = z
  .union([z.literal(''), isoDateTimeSchema])
  .nullish()
  .transform((v) => (v === '' ? null : v));

export const optionalUrlSchema = z
  .union([z.literal(''), z.string().trim().url('URL inválida.').max(500)])
  .nullish()
  .transform((v) => (v === '' ? null : v));

// ==================== autenticacao ==========================================

export const loginSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(1, 'Informe a senha.').max(200),
  })
  .strict();
export type LoginInput = z.infer<typeof loginSchema>;

export const requestPasswordResetSchema = z.object({ email: emailSchema }).strict();

export const confirmPasswordResetSchema = z
  .object({
    token: z.string().min(10).max(200),
    password: z.string().min(10, 'A senha deve ter ao menos 10 caracteres.').max(200),
  })
  .strict();

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(10, 'A senha deve ter ao menos 10 caracteres.').max(200),
  })
  .strict();

// ==================== empresas ==============================================

/**
 * Versao lida pelo cliente, para controle de concorrencia otimista.
 *
 * Opcional de proposito: script e integracao antigos continuam funcionando. O
 * inaceitavel seria o cliente enviar a versao e o servidor ignorar — prometer
 * protecao sem entregar e pior do que nao ter.
 */
export const versionSchema = z.coerce.number().int().min(1).optional();

export const accountCreateSchema = z
  .object({
    legalName: requiredText(200, 'Razão social'),
    tradeName: optionalText(200),
    cnpj: optionalCnpjSchema,
    domain: optionalText(255),
    status: z.enum(ACCOUNT_STATUS).default('PROSPECT'),
    segment: optionalText(120),
    sizeTier: z.enum(ACCOUNT_SIZE_TIER).nullish(),
    city: optionalText(120),
    state: optionalStateSchema,
    website: optionalUrlSchema,
    phone: optionalText(40),
    notes: optionalText(4000),
    economicGroupId: uuidSchema.nullish(),
    /** Cria o grupo economico se ainda nao existir. Reduz digitacao. */
    economicGroupName: optionalText(200),
    sourceId: uuidSchema.nullish(),
    ownerUserId: uuidSchema.nullish(),
    partnerId: uuidSchema.nullish(),
    /** Confirma a criacao mesmo com possivel duplicidade apontada. */
    acknowledgeDuplicates: z.boolean().default(false),
  })
  .strict();
export type AccountCreateInput = z.input<typeof accountCreateSchema>;

/**
 * Edicao de empresa.
 *
 * `economicGroupName` PERMANECE aceito aqui — e nao e detalhe.
 *
 * O formulario de empresa e o MESMO na criacao e na edicao, e ele sempre enviou
 * o nome do grupo economico (o campo permite criar o grupo digitando o nome).
 * O schema de edicao omitia essa chave e era `.strict()`: toda edicao de
 * empresa era recusada com "Unrecognized key(s) in object", que a interface
 * traduzia como "Campo nao reconhecido" — sem dizer QUAL campo, porque a
 * mensagem do zod para chave desconhecida nao tem caminho de campo.
 *
 * O servico ja sabia resolver o nome do grupo na edicao (`resolveEconomicGroup`
 * e chamado tanto na criacao quanto no update). Faltava o contrato deixar
 * passar. Retirar o campo da tela "resolveria" o erro e tiraria da pessoa a
 * unica forma de corrigir o grupo economico de uma empresa ja cadastrada.
 */
export const accountUpdateSchema = accountCreateSchema
  .omit({ acknowledgeDuplicates: true })
  .partial()
  .extend({ acknowledgeDuplicates: z.boolean().default(false) })
  .extend({ version: versionSchema })
  .strict();

export const accountListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** Busca por nome, nome fantasia, CNPJ ou dominio. */
  q: z.string().trim().max(200).optional(),
  status: z.enum(ACCOUNT_STATUS).optional(),
  partnerId: uuidSchema.optional(),
  ownerUserId: uuidSchema.optional(),
  economicGroupId: uuidSchema.optional(),
  state: stateSchema.optional(),
  segment: z.string().trim().max(120).optional(),
  /** CERES-025: a maturidade comercial, que é separada de `status`. */
  classification: z.enum(COMMERCIAL_CLASSIFICATION).optional(),
  /**
   * Visões rápidas: recortes do trabalho, combináveis com o resto.
   *
   * `minhaCarteira` é o meu; `semResponsavel` é o de ninguém; `semProximaAcao`
   * é o que vai vencer sem que ninguém perceba; `cadastroIncompleto` é o que
   * não dá para abordar.
   */
  view: z
    .enum(['minhaCarteira', 'semResponsavel', 'semProximaAcao', 'cadastroIncompleto'])
    .optional(),
  sort: z
    .enum([
      'legalName',
      '-legalName',
      'createdAt',
      '-createdAt',
      'updatedAt',
      '-updatedAt',
      'commercialClassification',
      '-commercialClassification',
    ])
    .optional(),
});

export const duplicateCheckSchema = z.object({
  legalName: z.string().trim().max(200).optional(),
  tradeName: z.string().trim().max(200).optional(),
  cnpj: z.string().trim().max(20).optional(),
  domain: z.string().trim().max(255).optional(),
  excludeAccountId: uuidSchema.optional(),
});

// ==================== contatos ==============================================

/**
 * Cadastro de contato.
 *
 * `accountId` é OPCIONAL: o contato pode ser independente. Ausente e nulo têm o
 * mesmo significado aqui (sem empresa) — o que a API precisa distinguir é isso de
 * um identificador inválido (422) ou de uma empresa fora do escopo (404).
 */
export const contactCreateSchema = z
  .object({
    accountId: uuidSchema.nullish(),
    name: requiredText(200, 'Nome'),
    jobTitle: optionalText(150),
    department: optionalText(150),
    email: optionalEmailSchema,
    phone: optionalText(40),
    mobile: optionalText(40),
    linkedinUrl: optionalUrlSchema,
    city: optionalText(120),
    state: optionalStateSchema,
    sourceId: uuidSchema.nullish(),
    ownerUserId: uuidSchema.nullish(),
    roleType: z.enum(CONTACT_ROLE_TYPE).default('USER'),
    isPrimary: z.boolean().default(false),
    status: z.enum(CONTACT_STATUS).default('ACTIVE'),
    notes: optionalText(4000),
    consentStatus: z.enum(CONSENT_STATUS).default('UNKNOWN'),
    consentBasis: optionalText(200),
    consentSource: optionalText(200),
    doNotEmail: z.boolean().default(false),
    doNotCall: z.boolean().default(false),
    doNotWhatsapp: z.boolean().default(false),
  })
  .strict();
export type ContactCreateInput = z.input<typeof contactCreateSchema>;

export const contactUpdateSchema = contactCreateSchema
  .omit({ accountId: true })
  .partial()
  .extend({ version: versionSchema })
  .strict();

/**
 * Associar, trocar ou REMOVER a empresa do contato.
 *
 * `accountId: null` remove a associação — o contato continua existindo, com todo
 * o histórico. É rota própria porque a operação tem regra e auditoria distintas
 * de uma edição de campo qualquer.
 */
export const contactSetAccountSchema = z
  .object({
    accountId: uuidSchema.nullable(),
  })
  .strict();

/** Vínculo do contato com empresa, para o filtro da listagem. */
export const CONTACT_LINKAGE = ['ALL', 'WITH_ACCOUNT', 'WITHOUT_ACCOUNT'] as const;
export type ContactLinkage = (typeof CONTACT_LINKAGE)[number];

export const contactListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(200).optional(),
  accountId: uuidSchema.optional(),
  linkage: z.enum(CONTACT_LINKAGE).optional(),
  roleType: z.enum(CONTACT_ROLE_TYPE).optional(),
  status: z.enum(CONTACT_STATUS).optional(),
  isPrimary: z.enum(['true', 'false']).optional(),
  ownerUserId: uuidSchema.optional(),
  sort: z.enum(['name', '-name', 'createdAt', '-createdAt', 'updatedAt', '-updatedAt']).optional(),
});

export const contactDuplicateCheckSchema = z
  .object({
    name: z.string().trim().max(200).optional(),
    email: z.string().trim().max(320).optional(),
    phone: z.string().trim().max(40).optional(),
    excludeId: uuidSchema.optional(),
  })
  .strict();

// ==================== pipelines =============================================

/**
 * Etapa de pipeline na entrada.
 *
 * NAO existe `sortOrder`: a ordem e a POSICAO no array, e o servidor a aplica a
 * partir do indice. Aceitar tambem um campo de ordenacao criaria ambiguidade
 * ("e se `sortOrder` discordar da posicao?") sem ganho algum.
 *
 * Antes de os schemas serem estritos, um cliente que enviasse `sortOrder` recebia
 * 200 e o campo era descartado em silencio — o pedido parecia atendido. Agora a
 * resposta e 422 dizendo exatamente qual chave nao existe.
 */
export const pipelineStageInputSchema = z
  .object({
    id: uuidSchema.optional(),
    key: requiredText(60, 'Chave da etapa').regex(
      /^[a-z0-9_]+$/,
      'A chave deve usar apenas letras minúsculas, números e sublinhado.',
    ),
    name: requiredText(120, 'Nome da etapa'),
    description: optionalText(500),
    probability: percentageSchema.default(0),
    stageType: z.enum(STAGE_TYPE).default('OPEN'),
    isActive: z.boolean().default(true),
    requiredFields: z.array(z.string().max(60)).max(20).default([]),
    requiredActions: z.array(z.string().max(60)).max(20).default([]),
    requiresConfirmation: z.boolean().default(false),
    confirmationMessage: optionalText(300),
    slaDays: z.coerce.number().int().min(0).max(3650).nullish(),
    /**
     * Criterio HUMANO de entrada e saida da etapa.
     *
     * `requiredFields` e `requiredActions` cobrem o que a maquina verifica. O que
     * faz a etapa significar alguma coisa — "o cliente confirmou orcamento e
     * prazo" — nao cabe em campo obrigatorio. Escrito, vira combinado de equipe;
     * nao escrito, cada vendedor usa o proprio.
     */
    entryCriteria: optionalText(600),
    exitCriteria: optionalText(600),
    /**
     * CERES-UX-001 — a FASE da jornada de Negócios a que esta etapa pertence.
     * Opcional: sem ela, a fase é derivada (ver `fases-do-negocio.ts`).
     */
    processPhase: z.enum(FASES_DO_NEGOCIO).nullish(),
    color: optionalText(20),
  })
  .strict();

export const pipelineCreateSchema = z
  .object({
    key: requiredText(60, 'Chave do pipeline').regex(
      /^[a-z0-9_]+$/,
      'Use apenas minúsculas, números e sublinhado.',
    ),
    name: requiredText(120, 'Nome do pipeline'),
    description: optionalText(500),
    isActive: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    /**
     * ESCOPO: a que parte do negocio este processo serve.
     *
     * Serve para o CRM RECOMENDAR o pipeline certo na criacao da oportunidade —
     * vender servico consultivo e vender assinatura sao processos diferentes, e
     * misturá-los num funil so e o que faz "taxa de conversao" virar um numero
     * que ninguem consegue usar. Nao e trava: o caso fora do padrao continua
     * possivel.
     */
    businessUnitId: uuidSchema.nullish(),
    offeringId: uuidSchema.nullish(),
    salesType: optionalText(80),
    status: z.enum(['RASCUNHO', 'ATIVO', 'INATIVO']).optional(),
    /** CERES-DP-006 (DP-16): permite pular etapas ao avançar (com motivo). */
    allowStageSkip: z.boolean().optional(),
    stages: z.array(pipelineStageInputSchema).min(2, 'O pipeline precisa de ao menos duas etapas.'),
  })
  .strict();

export const pipelineUpdateSchema = pipelineCreateSchema
  .partial()
  .extend({
    stages: z.array(pipelineStageInputSchema).min(2).optional(),
  })
  .strict();

// ==================== oportunidades =========================================

export const opportunityCreateSchema = z
  .object({
    /**
     * Excecao gerencial para criar oportunidade em empresa BLOQUEADA (BKC-019).
     *
     * Precisa ser explicita e justificada: a restricao nao pode ser um aviso que
     * se ignora clicando de novo. Vai para a auditoria.
     */
    blockedAccountOverride: z.boolean().optional(),
    blockedAccountOverrideReason: optionalText(1000),
    accountId: uuidSchema,
    pipelineId: uuidSchema.optional(),
    stageId: uuidSchema.optional(),
    title: requiredText(200, 'Título'),
    description: optionalText(4000),
    opportunityType: z.enum(OPPORTUNITY_TYPE),
    opportunityTypeOther: optionalText(120),
    ownerUserId: uuidSchema.optional(),
    sourcePartnerId: uuidSchema.nullish(),
    sourceId: uuidSchema.nullish(),
    estimatedValue: optionalMoneySchema,
    currency: z.string().length(3).toUpperCase().default('BRL'),
    recurrenceMonths: z.coerce.number().int().min(1).max(600).nullish(),
    probability: percentageSchema.optional(),
    expectedCloseDate: optionalIsoDateSchema,
    nextStep: optionalText(300),
    nextStepDueAt: optionalIsoDateTimeSchema,
    contactIds: z.array(uuidSchema).max(50).default([]),
    primaryContactId: uuidSchema.nullish(),
    offeringIds: z.array(uuidSchema).max(50).default([]),
  })
  .strict()
  .refine((v) => v.opportunityType !== 'OTHER' || (v.opportunityTypeOther ?? '').length > 0, {
    message: 'Descreva o tipo quando escolher "Outro tipo".',
    path: ['opportunityTypeOther'],
  });

export const opportunityUpdateSchema = z
  .object({
    title: requiredText(200, 'Título').optional(),
    description: optionalText(4000),
    opportunityType: z.enum(OPPORTUNITY_TYPE).optional(),
    opportunityTypeOther: optionalText(120),
    ownerUserId: uuidSchema.optional(),
    sourcePartnerId: uuidSchema.nullish(),
    sourceId: uuidSchema.nullish(),
    estimatedValue: optionalMoneySchema,
    recurrenceMonths: z.coerce.number().int().min(1).max(600).nullish(),
    probability: percentageSchema.optional(),
    expectedCloseDate: optionalIsoDateSchema,
    nextStep: optionalText(300),
    nextStepDueAt: optionalIsoDateTimeSchema,
    outcomeReasonId: uuidSchema.nullish(),
    outcomeNotes: optionalText(2000),
    competitorName: optionalText(200),
    contactIds: z.array(uuidSchema).max(50).optional(),
    primaryContactId: uuidSchema.nullish(),
    offeringIds: z.array(uuidSchema).max(50).optional(),
  })
  .extend({ version: versionSchema })
  .strict();

/**
 * Congelamento de oportunidade. (BKC-021)
 *
 * Motivo e data de revisao sao OBRIGATORIOS. Sem motivo, "congelada" e
 * indistinguivel de "esquecida"; sem data, ninguem revisita — e o congelamento
 * existe justamente para nao esquecer.
 */
export const opportunityFreezeSchema = z
  .object({
    reason: requiredText(500, 'Motivo').refine((valor) => valor.trim().length >= 10, {
      message:
        'Explique o motivo em pelo menos 10 caracteres — quem ler em três meses precisa entender.',
    }),
    reviewOn: isoDateSchema,
    version: versionSchema,
  })
  .strict();

export const opportunityReopenSchema = z
  .object({ note: optionalText(500), version: versionSchema })
  .strict();

/**
 * Divisao declarada do resultado. (BKC-022)
 *
 * Substitui a lista COMPLETA: a divisao so faz sentido como conjunto. Uma rota de
 * "adicionar participante" permitiria estados intermediarios somando 130%.
 */
export const opportunityParticipantsSchema = z
  .object({
    participants: z
      .array(
        z
          .object({
            partnerId: uuidSchema.nullish(),
            userId: uuidSchema.nullish(),
            contributionType: z.enum(CONTRIBUTION_TYPE),
            /** Percentual como string decimal (ADR-010). */
            sharePercentage: percentageStringSchema,
            notes: optionalText(500),
          })
          .strict(),
      )
      .max(10),
  })
  .strict();

export const opportunityMoveSchema = z
  .object({
    toStageId: uuidSchema,
    /** Confirmacao explicita para transicoes de impacto relevante. */
    confirmed: z.boolean().default(false),
    /** Controle de concorrencia otimista: evita sobrescrever movimento alheio. */
    expectedVersion: z.coerce.number().int().min(1).optional(),
    reason: optionalText(500),
    /** Obrigatorio ao mover para etapa terminal, se ainda nao definido. */
    outcomeReasonId: uuidSchema.nullish(),
    outcomeNotes: optionalText(2000),
  })
  .strict();

export const opportunityListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(200).optional(),
  pipelineId: uuidSchema.optional(),
  stageId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  ownerUserId: uuidSchema.optional(),
  sourcePartnerId: uuidSchema.optional(),
  opportunityType: z.enum(OPPORTUNITY_TYPE).optional(),
  status: z.enum(['OPEN', 'WON', 'LOST', 'FROZEN']).optional(),
  expectedCloseFrom: isoDateSchema.optional(),
  expectedCloseTo: isoDateSchema.optional(),
  sort: z
    .enum([
      'title',
      '-title',
      'estimatedValue',
      '-estimatedValue',
      'expectedCloseDate',
      '-expectedCloseDate',
      'updatedAt',
      '-updatedAt',
      'createdAt',
      '-createdAt',
    ])
    .optional(),
});

// ==================== atividades ============================================

export const activityCreateSchema = z
  .object({
    type: z.enum(ACTIVITY_TYPE),
    subject: requiredText(200, 'Assunto'),
    description: optionalText(4000),
    status: z.enum(ACTIVITY_STATUS).default('PLANNED'),
    priority: z.enum(ACTIVITY_PRIORITY).default('NORMAL'),
    dueAt: optionalIsoDateTimeSchema,
    completedAt: optionalIsoDateTimeSchema,
    assignedToUserId: uuidSchema.optional(),
    accountId: uuidSchema.nullish(),
    contactId: uuidSchema.nullish(),
    opportunityId: uuidSchema.nullish(),
    direction: z.enum(ACTIVITY_DIRECTION).nullish(),
    outcome: optionalText(1000),
    location: optionalText(200),
    durationMinutes: z.coerce.number().int().min(0).max(10_080).nullish(),
  })
  .strict()
  .refine(
    // `!= null` de propósito: cobre campo AUSENTE e campo nulo.
    (v) => v.accountId != null || v.contactId != null || v.opportunityId != null,
    { message: 'Vincule a atividade a uma empresa, contato ou oportunidade.', path: ['accountId'] },
  )
  .refine((v) => v.status !== 'PLANNED' || v.dueAt != null, {
    message: 'Informe o prazo da atividade planejada.',
    path: ['dueAt'],
  });

export const activityUpdateSchema = z
  .object({
    subject: requiredText(200, 'Assunto').optional(),
    description: optionalText(4000),
    priority: z.enum(ACTIVITY_PRIORITY).optional(),
    dueAt: optionalIsoDateTimeSchema,
    assignedToUserId: uuidSchema.optional(),
    direction: z.enum(ACTIVITY_DIRECTION).nullish(),
    outcome: optionalText(1000),
    location: optionalText(200),
    durationMinutes: z.coerce.number().int().min(0).max(10_080).nullish(),
  })
  .strict();

export const activityCompleteSchema = z
  .object({
    outcome: optionalText(1000),
    completedAt: optionalIsoDateTimeSchema,
  })
  .strict();

export const activityListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  type: z.enum(ACTIVITY_TYPE).optional(),
  status: z.enum(ACTIVITY_STATUS).optional(),
  assignedToUserId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  opportunityId: uuidSchema.optional(),
  contactId: uuidSchema.optional(),
  /** Filtro derivado: apenas atividades vencidas. */
  overdue: z.enum(['true', 'false']).optional(),
  dueFrom: optionalIsoDateTimeSchema,
  dueTo: optionalIsoDateTimeSchema,
  sort: z.enum(['dueAt', '-dueAt', 'createdAt', '-createdAt']).optional(),
});

// ==================== parceiros =============================================

export const partnerCreateSchema = z
  .object({
    /** Para PESSOA, é o nome completo; para EMPRESA, a razão social. */
    legalName: requiredText(200, 'Nome'),
    tradeName: optionalText(200),
    kind: z.enum(PARTNER_KIND).default('COMPANY'),
    /** CPF ou CNPJ. `cnpj` permanece aceito por compatibilidade. */
    documentNumber: optionalPartnerDocumentSchema,
    cnpj: optionalCnpjSchema,
    modality: z.enum(PARTNER_MODALITY).nullish(),
    modalityOther: optionalText(120),
    originAccountId: uuidSchema.nullish(),
    linkedContactId: uuidSchema.nullish(),
    status: z.enum(PARTNER_STATUS).default('PROSPECT'),
    internalOwnerUserId: uuidSchema.nullish(),
    tier: optionalText(60),
    contactEmail: optionalEmailSchema,
    contactPhone: optionalText(40),
    city: optionalText(120),
    state: optionalStateSchema,
    notes: optionalText(4000),
  })
  .strict()
  .refine((v) => v.modality !== 'OTHER' || (v.modalityOther ?? '').trim() !== '', {
    // "Outra" sem dizer qual é o mesmo que não registrar a modalidade, com a
    // desvantagem de parecer preenchido.
    message: 'Diga qual é a modalidade.',
    path: ['modalityOther'],
  })
  .refine(
    (v) => v.kind !== 'PERSON' || v.documentNumber == null || v.documentNumber.length === 11,
    {
      message: 'Parceiro pessoa usa CPF.',
      path: ['documentNumber'],
    },
  )
  .refine(
    (v) => v.kind !== 'COMPANY' || v.documentNumber == null || v.documentNumber.length === 14,
    {
      message: 'Parceiro empresa usa CNPJ.',
      path: ['documentNumber'],
    },
  );

export const partnerUpdateSchema = z
  .object({
    legalName: requiredText(200, 'Nome').optional(),
    tradeName: optionalText(200),
    kind: z.enum(PARTNER_KIND).optional(),
    documentNumber: optionalPartnerDocumentSchema,
    cnpj: optionalCnpjSchema,
    modality: z.enum(PARTNER_MODALITY).nullish(),
    modalityOther: optionalText(120),
    originAccountId: uuidSchema.nullish(),
    linkedContactId: uuidSchema.nullish(),
    status: z.enum(PARTNER_STATUS).optional(),
    internalOwnerUserId: uuidSchema.nullish(),
    tier: optionalText(60),
    contactEmail: optionalEmailSchema,
    contactPhone: optionalText(40),
    city: optionalText(120),
    state: optionalStateSchema,
    notes: optionalText(4000),
    version: versionSchema,
  })
  .strict();

export const partnerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(200).optional(),
  status: z.enum(PARTNER_STATUS).optional(),
  kind: z.enum(PARTNER_KIND).optional(),
  modality: z.enum(PARTNER_MODALITY).optional(),
  sort: z.enum(['legalName', '-legalName', 'createdAt', '-createdAt']).optional(),
});

/**
 * CRM-VAL-003 §21 — procura por alguém que JÁ ESTÁ no CERES.
 *
 * A pergunta antes de cadastrar: "esta pessoa/empresa já existe aqui?". Sem
 * ela, o mesmo consultor vira contato, empresa e parceiro, com três telefones
 * que divergem no primeiro mês.
 */
export const partnerLookupQuerySchema = z
  .object({
    q: z.string().trim().min(2).max(200),
    kind: z.enum(PARTNER_KIND).optional(),
  })
  .strict();

// ==================== reservas ==============================================

export const reservationRequestSchema = z
  .object({
    accountId: uuidSchema,
    partnerId: uuidSchema.optional(),
    partnerContractId: uuidSchema.nullish(),
    scopeType: z.enum(RESERVATION_SCOPE_TYPE).default('COMPANY'),
    scopeDescription: optionalText(500),
    justification: requiredText(2000, 'Justificativa'),
    contributionType: z.enum(CONTRIBUTION_TYPE),
    originDescription: optionalText(1000),
  })
  .strict();

export const reservationDecideSchema = z
  .object({
    decision: z.enum(['APPROVE', 'REJECT', 'REQUEST_CLARIFICATION']),
    notes: optionalText(2000),
    /** Aprovacao excepcional quando o limite de contas foi excedido. */
    exceptionalApproval: z.boolean().default(false),
    /** Aprovar apesar dos sinais de sobreposicao, registrando a decisao. */
    overrideConflict: z.boolean().default(false),
  })
  .strict();

export const reservationActivateSchema = z.object({
  notes: optionalText(2000),
  evidenceAttachmentId: uuidSchema.nullish(),
});

export const reservationLogUpdateSchema = z.object({
  notes: requiredText(2000, 'Descrição da atualização'),
  evidenceAttachmentId: uuidSchema.nullish(),
});

export const reservationExtendSchema = z.object({
  extensionDays: z.coerce.number().int().positive(),
  notes: requiredText(2000, 'Justificativa da prorrogação'),
  /**
   * CERES-DP-008 (DP-20): prorrogação ALÉM do limite da política. Só para quem
   * decide reservas, e a justificativa da exceção fica no histórico.
   */
  excecao: optionalText(1000),
});

export const reservationReleaseSchema = z.object({
  notes: requiredText(2000, 'Motivo da liberação'),
  /**
   * CERES-DP-008 (DP-20): cancelamento libera a empresa NA HORA, sem proteção
   * residual. Sem ele, a liberação segue a regra anterior.
   */
  cancelamento: z.boolean().optional(),
});

export const reservationListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  state: z.string().trim().max(400).optional(),
  partnerId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  /** Reservas proximas da expiracao, em dias. */
  expiringInDays: z.coerce.number().int().min(0).max(365).optional(),
  sort: z.enum(['requestedAt', '-requestedAt', 'expiresAt', '-expiresAt']).optional(),
});

export const conflictResolveSchema = z
  .object({
    resolutionType: z.enum(CONFLICT_RESOLUTION_TYPE),
    resolutionNotes: requiredText(2000, 'Justificativa da decisão'),
    awards: z
      .array(z.object({ partyId: uuidSchema, awardedSharePercentage: percentageSchema }))
      .max(10)
      .default([]),
  })
  .strict();

// ==================== usuarios / administracao ==============================

/**
 * Definicao de senha por administrador. (BKC-002)
 *
 * A senha e DIGITADA por quem administra e comunicada fora do sistema. Nao ha
 * geracao automatica de proposito: um segredo gerado teria de ser devolvido pela
 * API, e ai passaria por log de acesso, historico de navegador e captura de tela.
 *
 * `confirmation` existe para que um erro de digitacao nao produza uma conta com
 * senha que ninguem conhece — exatamente o problema que este recurso corrige.
 */
export const userSetPasswordSchema = z
  .object({
    password: z.string().min(1, 'Informe a senha.').max(200),
    confirmation: z.string().min(1, 'Repita a senha.').max(200),
    /**
     * Exigir troca no primeiro acesso. Verdadeiro por padrao: senha escolhida por
     * terceiro nao deveria virar a senha definitiva de ninguem.
     */
    temporary: z.boolean().default(true),
  })
  .strict();

/** Mudanca de situacao do usuario. As transicoes validas sao verificadas no servico. */
export const userChangeStatusSchema = z
  .object({
    status: z.enum(USER_STATUS),
  })
  .strict();

export const userCreateSchema = z
  .object({
    email: emailSchema,
    name: requiredText(200, 'Nome'),
    jobTitle: optionalText(150),
    phone: optionalText(40),
    /** Senha inicial. Ausente => usuario fica INVITED sem senha utilizavel. */
    password: z.string().min(10).max(200).optional(),
    partnerId: uuidSchema.nullish(),
    roleKeys: z.array(z.string().max(60)).min(1, 'Atribua ao menos um perfil.'),
    status: z.enum(['INVITED', 'ACTIVE']).default('ACTIVE'),
  })
  .strict();

export const userUpdateSchema = z
  .object({
    name: requiredText(200, 'Nome').optional(),
    jobTitle: optionalText(150),
    phone: optionalText(40),
    // Mudanca de status por PATCH passa pela MESMA maquina de transicoes do
    // endpoint dedicado — o schema aceita o enum inteiro; o servico decide.
    status: z.enum(USER_STATUS).optional(),
    roleKeys: z.array(z.string().max(60)).min(1).optional(),
  })
  .strict();

export const userListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(200).optional(),
  status: z.enum(USER_STATUS).optional(),
  partnerId: uuidSchema.optional(),
});

// ==================== auditoria =============================================

export const auditListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  entityType: z.string().trim().max(60).optional(),
  entityId: uuidSchema.optional(),
  actorUserId: uuidSchema.optional(),
  action: z.string().trim().max(60).optional(),
  correlationId: uuidSchema.optional(),
  occurredFrom: optionalIsoDateTimeSchema,
  occurredTo: optionalIsoDateTimeSchema,
});

// ==================== notas =================================================

export const noteCreateSchema = z
  .object({
    body: requiredText(8000, 'Conteúdo'),
    accountId: uuidSchema.nullish(),
    contactId: uuidSchema.nullish(),
    opportunityId: uuidSchema.nullish(),
    partnerId: uuidSchema.nullish(),
    reservationId: uuidSchema.nullish(),
  })
  .strict()
  .refine(
    (v) =>
      v.accountId != null ||
      v.contactId != null ||
      v.opportunityId != null ||
      v.partnerId != null ||
      v.reservationId != null,
    { message: 'Vincule a nota a um registro.', path: ['accountId'] },
  );

/**
 * TRANSFERENCIA DE PIPELINE (§7.1).
 *
 * O motivo e obrigatorio: uma oportunidade que troca de funil sem justificativa
 * estraga a serie historica dos dois — o de origem perde uma conversao que nao
 * aconteceu, e o de destino ganha um negocio cujo ciclo comecou em outro lugar.
 */
export const opportunityTransferPipelineSchema = z
  .object({
    toPipelineId: uuidSchema,
    toStageId: uuidSchema.optional(),
    reason: requiredText(500, 'Motivo da transferência'),
  })
  .strict();
export type OpportunityTransferPipelineInput = z.input<typeof opportunityTransferPipelineSchema>;

// ==================== catálogo comercial (CERES-004) ========================

/**
 * Quantidade comercial: string decimal com até 3 casas (horas fracionadas,
 * p.ex. 12.5h). String pelo mesmo motivo do dinheiro (ADR-010).
 */
export const quantitySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim().replace(',', '.')))
  .refine((v) => /^\d{1,11}(\.\d{1,3})?$/.test(v), 'Quantidade inválida.')
  .refine((v) => Number(v) > 0, 'A quantidade precisa ser maior que zero.');

export const periodCountSchema = z.coerce.number().int().min(1).max(600);

export const catalogItemCreateSchema = z
  .object({
    name: requiredText(200, 'Nome'),
    code: optionalText(60),
    kind: z.enum(CATALOG_ITEM_KIND),
    category: optionalText(120),
    description: optionalText(2000),
    pricingModel: z.enum(CATALOG_PRICING_MODEL),
    unit: optionalText(40),
    baseUnitPrice: moneySchema,
    currency: z.string().length(3).toUpperCase().default('BRL'),
    billingPeriodicity: z.enum(CATALOG_BILLING_PERIODICITY).nullish(),
    defaultQuantity: z
      .union([z.literal(''), quantitySchema])
      .nullish()
      .transform((v) => (v === '' ? null : v)),
    defaultPeriodCount: periodCountSchema.nullish(),
    estimatedHours: z
      .union([z.literal(''), quantitySchema])
      .nullish()
      .transform((v) => (v === '' ? null : v)),
  })
  .strict()
  .refine((v) => v.pricingModel !== 'SUBSCRIPTION' || v.billingPeriodicity != null, {
    message: 'Assinatura precisa de periodicidade.',
    path: ['billingPeriodicity'],
  });

export const catalogItemUpdateSchema = z
  .object({
    name: requiredText(200, 'Nome').optional(),
    code: optionalText(60),
    category: optionalText(120),
    description: optionalText(2000),
    unit: optionalText(40),
    baseUnitPrice: moneySchema.optional(),
    billingPeriodicity: z.enum(CATALOG_BILLING_PERIODICITY).nullish(),
    defaultQuantity: z
      .union([z.literal(''), quantitySchema])
      .nullish()
      .transform((v) => (v === '' ? null : v)),
    defaultPeriodCount: periodCountSchema.nullish(),
    estimatedHours: z
      .union([z.literal(''), quantitySchema])
      .nullish()
      .transform((v) => (v === '' ? null : v)),
    active: z.boolean().optional(),
  })
  .strict();

export const catalogItemListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  kind: z.enum(CATALOG_ITEM_KIND).optional(),
  // Aceita string E boolean: o fastify valida a querystring com este mesmo
  // schema e entrega o valor já transformado; o re-parse da rota precisa
  // aceitar a própria saída.
  active: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .transform((v) => v === true || v === 'true')
    .optional(),
  category: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

/**
 * Adição de item à oportunidade. `negotiatedUnitPrice` é OPCIONAL: sem ele, o
 * item nasce com o preço de referência do catálogo (snapshot). Informá-lo
 * diferente da referência exige `opportunity_price:override` (o servidor
 * valida — o front nunca é a única defesa).
 */
export const opportunityItemAddSchema = z
  .object({
    catalogItemId: uuidSchema,
    quantity: quantitySchema.optional(),
    periodCount: periodCountSchema.optional(),
    negotiatedUnitPrice: moneySchema.optional(),
    priceChangeReason: optionalText(500),
  })
  .strict();

export const opportunityItemUpdateSchema = z
  .object({
    quantity: quantitySchema.optional(),
    periodCount: periodCountSchema.optional(),
    negotiatedUnitPrice: moneySchema.optional(),
    priceChangeReason: optionalText(500),
  })
  .strict();

/**
 * CERES-034 — CONDIÇÕES COMERCIAIS DA OPORTUNIDADE.
 *
 * Todos os campos são opcionais e aceitam `null`: a oportunidade antiga não
 * passa a exigir nada, e limpar uma condição é uma operação legítima.
 *
 * `billingModel` reaproveita a enumeração do Catálogo em vez de criar uma
 * segunda lista de modelos de cobrança — duas listas divergem no primeiro mês.
 */
export const opportunityCommercialConditionsSchema = z
  .object({
    commercialValidUntil: optionalIsoDateSchema,
    paymentTerms: optionalText(300),
    deliveryEstimate: optionalText(300),
    commercialNotes: optionalText(4000),
    billingModel: z.enum(CATALOG_PRICING_MODEL).nullish(),
    billingPeriodicity: z.enum(CATALOG_BILLING_PERIODICITY).nullish(),
    version: versionSchema,
  })
  .strict();

/**
 * Pedido de aprovação comercial.
 *
 * O motivo é OBRIGATÓRIO e com tamanho mínimo: quem decide precisa saber por
 * que este desconto, neste negócio. "Desconto" como justificativa é a mesma
 * coisa que nenhuma justificativa, e produz uma fila de pedidos indecidíveis.
 */
export const commercialApprovalRequestSchema = z
  .object({
    reason: requiredText(1000, 'Justificativa').refine((valor) => valor.trim().length >= 10, {
      message: 'Explique em pelo menos 10 caracteres por que este cenário precisa de exceção.',
    }),
    version: versionSchema,
  })
  .strict();

/**
 * Decisão sobre o pedido.
 *
 * Recusar ou pedir ajustes EXIGE comentário — devolver um cenário sem dizer o
 * que corrigir faz ele voltar igual na semana seguinte. Aprovar não exige: o
 * cenário aprovado já está congelado no próprio pedido.
 */
export const commercialApprovalDecisionSchema = z
  .object({
    decision: z.enum(COMMERCIAL_APPROVAL_DECISION),
    comment: optionalText(2000),
  })
  .strict()
  .refine((v) => v.decision === 'APPROVED' || (v.comment ?? '').trim().length >= 10, {
    message: 'Diga em pelo menos 10 caracteres o que precisa mudar.',
    path: ['comment'],
  });

/**
 * CERES-034 — regra de alçada de desconto.
 *
 * O alvo é um PAPEL ou uma PESSOA, nunca os dois: com os dois preenchidos, a
 * precedência viraria adivinhação. `unlimited` e `maxDiscountPercent` também se
 * excluem — alçada ilimitada é uma decisão, e não o número 100.
 */
export const discountAuthorityRuleCreateSchema = z
  .object({
    roleId: uuidSchema.nullish(),
    userId: uuidSchema.nullish(),
    maxDiscountPercent: z
      .union([z.literal(''), z.string(), z.number()])
      .nullish()
      .transform((v) =>
        v === '' || v === null || v === undefined
          ? null
          : typeof v === 'number'
            ? v.toFixed(2)
            : v.trim().replace(',', '.'),
      )
      .refine((v) => v === null || /^\d{1,3}(\.\d{1,2})?$/.test(v), 'Percentual inválido.')
      .refine((v) => v === null || (Number(v) >= 0 && Number(v) <= 100), {
        message: 'O percentual precisa estar entre 0 e 100.',
      }),
    unlimited: z.boolean().default(false),
    validFrom: optionalIsoDateSchema,
    validTo: optionalIsoDateSchema,
    notes: optionalText(500),
  })
  .strict()
  .refine((v) => (v.roleId == null) !== (v.userId == null), {
    message: 'Escolha um perfil OU uma pessoa — nunca os dois.',
    path: ['roleId'],
  })
  .refine((v) => (v.unlimited ? v.maxDiscountPercent === null : v.maxDiscountPercent !== null), {
    message: 'Informe o percentual máximo, ou marque a alçada como ilimitada.',
    path: ['maxDiscountPercent'],
  });

export const discountAuthorityRuleUpdateSchema = z
  .object({
    maxDiscountPercent: z
      .union([z.literal(''), z.string(), z.number()])
      .nullish()
      .transform((v) =>
        v === '' || v === null || v === undefined
          ? null
          : typeof v === 'number'
            ? v.toFixed(2)
            : v.trim().replace(',', '.'),
      )
      .refine((v) => v === null || /^\d{1,3}(\.\d{1,2})?$/.test(v), 'Percentual inválido.')
      .refine((v) => v === null || (Number(v) >= 0 && Number(v) <= 100), {
        message: 'O percentual precisa estar entre 0 e 100.',
      }),
    unlimited: z.boolean().optional(),
    /**
     * `false` ENCERRA a regra (sem sucessora). Ausente ou `true` = a edição
     * normal, que encerra a regra atual e cria a sucessora com os valores
     * novos — a vigência nunca é sobrescrita.
     */
    active: z.boolean().optional(),
    validTo: optionalIsoDateSchema,
    notes: optionalText(500),
    /** Justificativa da alteração. Vai para a auditoria, não para a regra. */
    reason: optionalText(500),
  })
  .strict();

/**
 * Troca EXPLÍCITA da fonte do valor da oportunidade (CERES-004): passar a
 * calcular pela soma dos itens, ou voltar ao valor manual. Nunca acontece em
 * silêncio — é uma decisão de quem edita a oportunidade.
 */
export const opportunityValueSourceSchema = z
  .object({
    valueSource: z.enum(OPPORTUNITY_VALUE_SOURCE),
    /** Ao voltar para MANUAL, o valor a manter (default: a soma atual). */
    estimatedValue: optionalMoneySchema,
    version: versionSchema,
  })
  .strict();

// ==========================================================================
// CERES-036 — REGISTRO DOCUMENTAL DE PROPOSTAS (Fase 2)
//
// Os dois esquemas de CRIAÇÃO chegam por `multipart/form-data`, junto com o
// arquivo: os campos vêm como texto e são convertidos aqui, no mesmo lugar em
// que a web valida o formulário. `z.coerce` e as uniões com `''` existem por
// isso — um formulário não tem `undefined`, tem campo vazio.
// ==========================================================================

/**
 * Registrar uma proposta produzida fora do CERES.
 *
 * A oportunidade é OBRIGATÓRIA e a empresa vem dela — nunca informada em
 * separado. Proposta avulsa, ou de um lead sem oportunidade, não existe: sem a
 * oportunidade não há empresa validada, não há cenário comercial para congelar
 * e não há onde a proposta apareça depois.
 */
export const proposalRegisterSchema = z
  .object({
    opportunityId: uuidSchema,
    title: requiredText(200, 'Título'),
    ownerUserId: uuidSchema.nullish(),
    primaryContactId: uuidSchema.nullish(),
    externalReference: optionalText(120),
    declaredTotal: optionalMoneySchema,
    currency: z.string().trim().length(3).toUpperCase().optional(),
    validUntil: optionalIsoDateSchema,
    issuedOn: optionalIsoDateSchema,
    notes: optionalText(2000),
    /**
     * `READY` só é aceito com cenário comercial aprovado ou sem necessidade de
     * aprovação. O servidor confere — a tela apenas evita a viagem.
     */
    status: z.enum(['DRAFT', 'READY']).default('DRAFT'),
    /**
     * Justificativa da DIVERGÊNCIA de valor. Obrigatória quando o valor
     * informado difere do total negociado da oportunidade — e o que ela faz é
     * registrar a diferença, jamais corrigir um dos dois lados.
     */
    divergenceReason: optionalText(500),
    /** A proposta JÁ FOI ENVIADA antes de chegar aqui. */
    alreadySent: z.coerce.boolean().default(false),
    sentAt: optionalIsoDateTimeSchema,
    sentChannel: z.enum(PROPOSAL_SEND_CHANNEL).nullish(),
    sentRecipient: optionalText(200),
    /**
     * Confirmação EXPLÍCITA de que se está registrando um envio ocorrido sem
     * aprovação comercial válida. Sem ela o registro é recusado: a irregularidade
     * pode ser registrada, mas nunca por descuido.
     */
    confirmSendWithoutApproval: z.coerce.boolean().default(false),
    sendWithoutApprovalReason: optionalText(500),
  })
  .strict();

/**
 * Nova versão a partir da oportunidade ATUAL.
 *
 * Exige arquivo novo e motivo: substituir o arquivo de uma versão emitida
 * apagaria a resposta para "o que exatamente o cliente recebeu?".
 */
export const proposalVersionCreateSchema = z
  .object({
    changeReason: requiredText(500, 'Motivo da nova versão'),
    declaredTotal: optionalMoneySchema,
    validUntil: optionalIsoDateSchema,
    issuedOn: optionalIsoDateSchema,
    notes: optionalText(2000),
    status: z.enum(['DRAFT', 'READY']).default('DRAFT'),
    divergenceReason: optionalText(500),
  })
  .strict();

/** Metadados editáveis enquanto a versão vigente é rascunho. */
export const proposalMetadataUpdateSchema = z
  .object({
    title: requiredText(200, 'Título').optional(),
    ownerUserId: uuidSchema.nullish(),
    primaryContactId: uuidSchema.nullish(),
    externalReference: optionalText(120),
    notes: optionalText(2000),
    validUntil: optionalIsoDateSchema,
    issuedOn: optionalIsoDateSchema,
    declaredTotal: optionalMoneySchema,
    divergenceReason: optionalText(500),
  })
  .strict();

/**
 * Registro de um envio que aconteceu FORA do CERES.
 *
 * Não dispara comunicação, não confirma entrega e não marca como visualizada —
 * fingir qualquer uma das três transformaria um relato em evidência falsa.
 */
export const proposalSendRecordSchema = z
  .object({
    versionId: uuidSchema.optional(),
    channel: z.enum(PROPOSAL_SEND_CHANNEL),
    sentAt: isoDateTimeSchema,
    contactId: uuidSchema.nullish(),
    recipient: optionalText(200),
    note: optionalText(1000),
    confirmWithoutApproval: z.boolean().default(false),
    justification: optionalText(500),
  })
  .strict();

/**
 * Aceite, recusa, cancelamento e expiração.
 *
 * CERES-044 — A VERSÃO É EXPLÍCITA. Sem ela vale a vigente, resolvida no
 * servidor; com ela, o desfecho aponta a versão EXATA que o cliente aceitou ou
 * recusou. A vigente de hoje pode não ser a que foi enviada, e um aceite preso
 * à vigente afirmaria que o cliente aprovou um documento que nunca viu.
 */
export const proposalOutcomeSchema = z
  .object({
    outcome: z.enum(PROPOSAL_OUTCOME),
    versionId: uuidSchema.optional(),
    occurredAt: optionalIsoDateTimeSchema,
    /** Obrigatório em recusa e cancelamento — "não" sem motivo não ensina nada. */
    reason: optionalText(500),
    /** CERES-044: motivo CONTROLADO da recusa, para a leitura agregada. */
    rejectionReason: z.enum(PROPOSAL_REJECTION_REASON).optional(),
    note: optionalText(1000),
    /** Quem aceitou, quando é um contato cadastrado. */
    acceptedByContactId: uuidSchema.nullish(),
    acceptedByName: optionalText(200),
    /** Cargo de quem aceitou, quando informado. */
    acceptedByRole: optionalText(160),
    /** Referência externa do aceite: pedido de compra, protocolo, contrato. */
    externalReference: optionalText(200),
    /**
     * A evidência: um anexo que JÁ EXISTE no CRM.
     *
     * Referência, e não upload: o arquivo continua onde foi enviado, com o dono
     * e a trilha que já tinha. Um segundo caminho de upload aqui criaria uma
     * cópia sem história.
     */
    evidenciaAnexoId: uuidSchema.nullish(),
  })
  .strict();

// ===========================================================================
// CERES-044 — ACOMPANHAMENTO E PRÓXIMA AÇÃO
// ===========================================================================

/**
 * Uma próxima ação da proposta.
 *
 * Vira uma ATIVIDADE no sistema de tarefas que já existe — por isso data,
 * responsável e tipo, e nada além: um segundo cadastro de tarefa daria duas
 * caixas de entrada ao mesmo vendedor.
 */
export const proposalNextActionSchema = z
  .object({
    tipo: z.enum(PROPOSAL_NEXT_ACTION_TYPE),
    /** Dia combinado. O horário é opcional — nem todo retorno tem hora marcada. */
    quandoIso: isoDateTimeSchema,
    responsavelId: uuidSchema.optional(),
    observacao: optionalText(1000),
  })
  .strict();

/**
 * Registrar um acompanhamento que ACONTECEU.
 *
 * Registrar acompanhamento não é reenviar a proposta, e não muda valor, versão
 * nem situação comercial: é a conversa, anotada onde ela pode ser lida depois.
 */
export const proposalFollowUpSchema = z
  .object({
    canal: z.enum(PROPOSAL_FOLLOWUP_CHANNEL),
    quandoIso: optionalIsoDateTimeSchema,
    contatoId: uuidSchema.nullish(),
    resumo: z.string().trim().min(3).max(2000),
    resultado: optionalText(2000),
    /** A próxima ação combinada na mesma conversa, quando houve uma. */
    proximaAcao: proposalNextActionSchema.optional(),
  })
  .strict();

/** Concluir uma próxima ação, com o resultado e — se houver — a seguinte. */
export const proposalActionDoneSchema = z
  .object({
    resultado: optionalText(2000),
    proximaAcao: proposalNextActionSchema.optional(),
  })
  .strict();

/** Marcar a versão vigente como pronta para envio. Exige cenário aprovado. */
export const proposalReadySchema = z.object({}).strict();

/** Arquivamento e restauração do registro lógico. */
export const proposalArchiveSchema = z
  .object({
    reason: optionalText(500),
    /** Confirmação explícita: arquivar sem perguntar é o defeito que não se repete. */
    confirm: z.literal(true),
  })
  .strict();

// ===========================================================================
// CERES-038 — BIBLIOTECA DE MODELOS DE PROPOSTA
// ===========================================================================

/**
 * Cor em hexadecimal de seis dígitos.
 *
 * Só isso. `rgb()`, `hsl()` e nomes de cor abririam caminho para uma string
 * que o gerador de PDF não sabe interpretar — e a falha apareceria como
 * documento sem cor nenhuma, não como erro.
 */
export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use uma cor em hexadecimal, como #1F2937.');

export const proposalTemplateRepeatSchema = z
  .object({
    fonte: z.literal('ITENS'),
    colunas: z
      .array(
        z.enum([
          'NOME',
          'DESCRICAO',
          'QUANTIDADE',
          'UNIDADE',
          'PRECO_REFERENCIA',
          'PRECO_NEGOCIADO',
          'DESCONTO',
          'SUBTOTAL',
        ]),
      )
      .min(1, 'Escolha ao menos uma coluna para a tabela.')
      .max(8),
    mostrarTotal: z.boolean().default(true),
  })
  .strict();

/**
 * A tabela simples (CERES-039).
 *
 * Os tetos não são decoração: 12 colunas é o que cabe legível em A4, e 100
 * linhas é o ponto em que a coisa deixou de ser tabela e virou planilha. Sem
 * eles, o documento sai com texto cortado e ninguém vê isso na edição.
 *
 * As células são TEXTO. Nenhum HTML, nenhum CSS, nenhum script, nada
 * incorporado — `<` e `>` não são sintaxe aqui, e saem impressos como texto.
 */
export const proposalTemplateTableColumnSchema = z
  .object({
    rotulo: z.string().trim().max(120).default(''),
    alinhamento: z.enum(PROPOSAL_TABLE_ALIGNMENT).default('ESQUERDA'),
    peso: z.number().int().min(1).max(12).default(1),
  })
  .strict();

export const proposalTemplateSimpleTableSchema = z
  .object({
    colunas: z
      .array(proposalTemplateTableColumnSchema)
      .min(1, 'A tabela precisa de ao menos uma coluna.')
      .max(
        PROPOSAL_TABLE_LIMITES.colunas,
        `A tabela aceita no máximo ${PROPOSAL_TABLE_LIMITES.colunas} colunas.`,
      ),
    linhas: z
      .array(
        z
          .array(z.string().max(PROPOSAL_TABLE_LIMITES.caracteresPorCelula))
          .max(PROPOSAL_TABLE_LIMITES.colunas),
      )
      .max(
        PROPOSAL_TABLE_LIMITES.linhas,
        `A tabela aceita no máximo ${PROPOSAL_TABLE_LIMITES.linhas} linhas.`,
      ),
    comCabecalho: z.boolean().default(true),
    enfatizarPrimeiraColuna: z.boolean().default(false),
  })
  .strict();

/**
 * CERES-040 — A APRESENTAÇÃO DA IMAGEM.
 *
 * Os `default` não são decoração: o conteúdo gravado antes da CERES-040 não
 * tem nenhum destes campos, e é o padrão que faz um modelo de 2026-09 abrir e
 * renderizar sem que ninguém precise editá-lo e republicá-lo.
 */
export const proposalTemplateImageSchema = z
  .object({
    alinhamento: z.enum(PROPOSAL_IMAGE_ALIGNMENT).default('CENTRO'),
    largura: z.enum(PROPOSAL_IMAGE_WIDTH).default('TOTAL'),
    ajuste: z.enum(PROPOSAL_IMAGE_FIT).default('CONTER'),
    legenda: optionalText(200),
    textoAlternativo: optionalText(200),
    manterLegendaJunto: z.boolean().default(true),
  })
  .strict();

export const proposalTemplateTypographySchema = z
  .object({
    escala: z.enum(PROPOSAL_TYPE_SCALE).default('NORMAL'),
    /** Abaixo de 1,0 as linhas se tocam; acima de 2,0 o texto se desmancha. */
    entrelinha: z.number().min(1).max(2).default(1.35),
    estiloDosTitulos: z.enum(PROPOSAL_TITLE_STYLE).default('SOBRIO'),
    espacoAntesDoTitulo: z.number().min(0).max(40).default(10),
    espacoDepoisDoTitulo: z.number().min(0).max(40).default(6),
  })
  .strict();

export const proposalTemplateTableStyleSchema = z
  .object({
    corDoCabecalho: hexColorSchema.nullish(),
    corDoTextoDoCabecalho: hexColorSchema.default('#111827'),
    linhasAlternadas: z.boolean().default(false),
    corDaLinhaAlternada: hexColorSchema.default('#F9FAFB'),
    bordas: z.enum(PROPOSAL_TABLE_BORDER).default('HORIZONTAIS'),
    corDasBordas: hexColorSchema.default('#E5E7EB'),
    espacamentoInterno: z.enum(PROPOSAL_TABLE_PADDING).default('NORMAL'),
    destacarTotal: z.boolean().default(true),
    repetirCabecalho: z.boolean().default(true),
  })
  .strict();

export const proposalTemplateHeaderSchema = z
  .object({
    mostrarLogo: z.boolean().default(false),
    alinhamentoDoLogo: z.enum(PROPOSAL_IMAGE_ALIGNMENT).default('ESQUERDA'),
    tamanhoDoLogo: z.enum(PROPOSAL_LOGO_SIZE).default('PEQUENO'),
    textoEsquerda: optionalText(120),
    textoCentro: optionalText(120),
    textoDireita: optionalText(120),
    mostrarNumeroDaProposta: z.boolean().default(false),
    mostrarCliente: z.boolean().default(false),
    linhaDivisoria: z.boolean().default(false),
    corDoTexto: hexColorSchema.default('#9CA3AF'),
    corDaLinha: hexColorSchema.default('#E5E7EB'),
    /** Milímetros, e não pontos: é a unidade em que a margem já é pedida. */
    espacoSuperiorMm: z.number().min(0).max(30).default(8),
    espacoInferiorMm: z.number().min(0).max(30).default(4),
  })
  .strict();

export const proposalTemplateFooterSchema = z
  .object({
    textoEsquerda: optionalText(160),
    textoCentro: optionalText(160),
    textoDireita: optionalText(160),
    mostrarConfidencialidade: z.boolean().default(true),
    mostrarNumeroDaProposta: z.boolean().default(false),
    mostrarData: z.boolean().default(false),
    formatoDaPaginacao: z.enum(PROPOSAL_PAGE_NUMBER_FORMAT).default('PAGINA_X_DE_Y'),
    linhaDivisoria: z.boolean().default(false),
    corDoTexto: hexColorSchema.default('#9CA3AF'),
    corDaLinha: hexColorSchema.default('#E5E7EB'),
    espacoSuperiorMm: z.number().min(0).max(30).default(4),
    espacoInferiorMm: z.number().min(0).max(30).default(6),
  })
  .strict();

export const proposalTemplateBlockSchema = z
  .object({
    id: uuidSchema,
    tipo: z.enum(PROPOSAL_BLOCK_KIND),
    titulo: optionalText(160),
    subtitulo: optionalText(200),
    mostrarTitulo: z.boolean().default(true),
    origem: z.enum(PROPOSAL_BLOCK_SOURCE).default('FIXED'),
    /**
     * 20 000 caracteres é bastante para uma seção e pouco para um livro. Sem
     * teto, um modelo colado de um DOCX inteiro produziria um PDF que nunca
     * termina de renderizar.
     */
    conteudo: z.string().max(20_000).default(''),
    repeticao: proposalTemplateRepeatSchema.nullish(),
    imagemAssetId: uuidSchema.nullish(),
    tabela: proposalTemplateSimpleTableSchema.nullish(),
    imagem: proposalTemplateImageSchema.default({}),
    obrigatorio: z.boolean().default(false),
    editavelNaProposta: z.boolean().default(false),
    visivelPorPadrao: z.boolean().default(true),
    oculto: z.boolean().default(false),
    quebraAntes: z.boolean().default(false),
    quebraDepois: z.boolean().default(false),
    evitarQuebraInterna: z.boolean().default(false),
    manterComProximo: z.boolean().default(false),
    estilo: z.enum(PROPOSAL_BLOCK_STYLE).default('NORMAL'),
    /**
     * CERES-041 — seção única ou uma seção por serviço negociado.
     *
     * `default` no valor antigo: um modelo publicado antes desta missão não tem
     * o campo, e precisa continuar produzindo exatamente o documento que
     * produzia.
     */
    apresentacao: z.enum(PROPOSAL_BLOCK_PRESENTATION).default('CONSOLIDADA'),
    /**
     * O serviço a que uma seção expandida pertence.
     *
     * Aceito aqui só para que a edição do modelo possa devolver o que leu sem
     * ser recusada por um campo a mais — o `.strict()` acima não perdoa. O
     * servidor o força a `null` ao gravar: a biblioteca não conhece
     * oportunidade nenhuma, e a expansão acontece dentro do rascunho.
     */
    servico: z
      .object({
        opportunityItemId: uuidSchema.nullish(),
        nome: z.string().trim().max(200),
        indice: z.number().int().min(0).max(200),
      })
      .strict()
      .nullish(),
    observacaoInterna: optionalText(500),
    condicao: optionalText(120),
  })
  .strict();

export const proposalTemplateThemeSchema = z
  .object({
    logoAssetId: uuidSchema.nullish(),
    logoAlternativoAssetId: uuidSchema.nullish(),
    corPrimaria: hexColorSchema.default('#111827'),
    corSecundaria: hexColorSchema.default('#374151'),
    corDeDestaque: hexColorSchema.default('#1D4ED8'),
    corDoTexto: hexColorSchema.default('#374151'),
    corDeFundo: hexColorSchema.nullish(),
    fonteDeTitulos: z.enum(PROPOSAL_TEMPLATE_FONT).default('HELVETICA'),
    fonteDeCorpo: z.enum(PROPOSAL_TEMPLATE_FONT).default('HELVETICA'),
    /** Abaixo de 8pt não se lê impresso; acima de 14pt a proposta vira cartaz. */
    tamanhoBase: z.number().min(8).max(14).default(10.5),
    /** Margem em milímetros. Menos de 10mm some na impressão. */
    margemMm: z.number().min(10).max(40).default(20),
    cabecalhoAtivo: z.boolean().default(true),
    rodapeAtivo: z.boolean().default(true),
    ocultarNaCapa: z.boolean().default(true),
    numerarPaginas: z.boolean().default(true),
    textoDoCabecalho: optionalText(120),
    textoDoRodape: optionalText(160),
    confidencialidade: optionalText(80),
    tipografia: proposalTemplateTypographySchema.default({}),
    tabela: proposalTemplateTableStyleSchema.default({}),
    cabecalho: proposalTemplateHeaderSchema.default({}),
    rodape: proposalTemplateFooterSchema.default({}),
  })
  .strict();

export const proposalTemplateCoverSchema = z
  .object({
    ativa: z.boolean().default(true),
    layout: z.enum(PROPOSAL_COVER_LAYOUT).default('INSTITUCIONAL'),
    imagemAssetId: uuidSchema.nullish(),
    imagemDeFundoAssetId: uuidSchema.nullish(),
    corDeFundo: hexColorSchema.nullish(),
    corDoTexto: hexColorSchema.nullish(),
    mostrarFaixa: z.boolean().default(true),
    corDaFaixa: hexColorSchema.nullish(),
    mostrarLogo: z.boolean().default(true),
    titulo: z.string().trim().max(160).default('Proposta comercial'),
    subtitulo: optionalText(200),
    mostrarCliente: z.boolean().default(true),
    mostrarOportunidade: z.boolean().default(true),
    mostrarNumero: z.boolean().default(true),
    mostrarData: z.boolean().default(true),
    mostrarValidade: z.boolean().default(true),
    mostrarResponsavel: z.boolean().default(true),
    textoComplementar: optionalText(300),
    mostrarCabecalho: z.boolean().default(false),
    mostrarRodape: z.boolean().default(false),
    mostrarNumeracao: z.boolean().default(false),
    alinhamento: z.enum(PROPOSAL_COVER_ALIGNMENT).default('ESQUERDA'),
  })
  .strict();

export const proposalTemplateContentSchema = z
  .object({
    blocos: z.array(proposalTemplateBlockSchema).max(80),
    tema: proposalTemplateThemeSchema,
    capa: proposalTemplateCoverSchema,
  })
  .strict();

export const proposalTemplateScopeSchema = z
  .object({
    tipo: z.enum(PROPOSAL_TEMPLATE_SCOPE).default('GENERIC'),
    kinds: z.array(z.enum(CATALOG_ITEM_KIND)).max(4).default([]),
    categorias: z.array(z.string().trim().min(1).max(120)).max(30).default([]),
    ofertas: z.array(uuidSchema).max(60).default([]),
  })
  .strict();

/** Criar o modelo: só a identificação. A estrutura vem no editor. */
export const proposalTemplateCreateSchema = z
  .object({
    nome: requiredText(160, 'Nome'),
    descricao: optionalText(1000),
    finalidade: z.enum(PROPOSAL_TEMPLATE_PURPOSE).default('GENERIC'),
    estiloVisual: z.enum(PROPOSAL_VISUAL_STYLE).default('CUSTOM'),
    caracteristicasVisuais: z.array(z.enum(PROPOSAL_VISUAL_TRAIT)).max(6).default([]),
    aplicabilidade: proposalTemplateScopeSchema.optional(),
  })
  .strict();

/** Salvar o rascunho: identificação e/ou conteúdo, em PATCH parcial. */
export const proposalTemplateUpdateSchema = z
  .object({
    nome: requiredText(160, 'Nome').optional(),
    descricao: optionalText(1000),
    finalidade: z.enum(PROPOSAL_TEMPLATE_PURPOSE).optional(),
    estiloVisual: z.enum(PROPOSAL_VISUAL_STYLE).optional(),
    caracteristicasVisuais: z.array(z.enum(PROPOSAL_VISUAL_TRAIT)).max(6).optional(),
    aplicabilidade: proposalTemplateScopeSchema.optional(),
    conteudo: proposalTemplateContentSchema.optional(),
    notas: optionalText(500),
  })
  .strict();

export const proposalTemplateDuplicateSchema = z
  .object({ nome: requiredText(160, 'Nome') })
  .strict();

export const proposalTemplateArchiveSchema = z
  .object({
    confirm: z.literal(true, { errorMap: () => ({ message: 'Confirme o arquivamento.' }) }),
  })
  .strict();

/** Prévia: sem oportunidade usa dados demonstrativos; com ela, só visualiza. */
export const proposalTemplatePreviewSchema = z
  .object({ oportunidadeId: uuidSchema.nullish() })
  .strict();

// ===========================================================================
// CERES-039 — GERAÇÃO ASSISTIDA DE PROPOSTA
// ===========================================================================

/**
 * Criar o rascunho.
 *
 * NENHUM CAMPO COMERCIAL AQUI, de propósito. Preço, desconto, quantidade,
 * total, moeda, condição e validade vêm da composição da oportunidade — a
 * fonte canônica da CERES-034. Aceitá-los no corpo faria a tela poder propor
 * um número que a alçada nunca viu, e a aprovação passaria a autorizar um
 * cenário diferente do que sai no documento.
 */
export const proposalGenerationCreateSchema = z
  .object({
    oportunidadeId: uuidSchema,
    /** Sem modelo escolhido vale o recomendado — que o servidor resolve. */
    modeloId: uuidSchema.optional(),
    contatoPrincipalId: uuidSchema.nullish(),
    titulo: optionalText(200),
    finalidade: z.enum(PROPOSAL_PURPOSE).optional(),
    /** CERES-PRP-011: a estrutura de blocos. Ausente = a sugerida pelos itens. */
    pacoteDeConteudo: z.enum(PROPOSAL_CONTENT_PACKAGE).optional(),
    chaveDaOperacao: uuidSchema.optional(),
  })
  .strict();

/**
 * CERES-PRP-011 — destinatário e validade do rascunho.
 *
 * São os dois dados que a pessoa precisa conseguir acertar ANTES de finalizar
 * sem voltar à oportunidade: trocar o contato que recebe e informar a validade
 * que a oportunidade não tinha. Nada comercial (preço, desconto, condição)
 * entra aqui.
 */
export const proposalDraftDataUpdateSchema = z
  .object({
    contatoPrincipalId: uuidSchema.nullable().optional(),
    validadeIso: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
  })
  .strict();

/** Salvar o texto de um bloco. */
export const proposalDraftBlockUpdateSchema = z
  .object({
    /** Texto com a marcação leve de sempre. HTML é limpo no servidor. */
    conteudo: z.string().max(20_000),
  })
  .strict();

/**
 * Pedir uma sugestão para UM bloco.
 *
 * `orientacao` vale só para esta geração e é tratada como instrução
 * EDITORIAL: ela muda o tom e o recorte do texto, nunca um dado comercial.
 * `confirmarSubstituicao` é a confirmação de quem já tinha texto ali.
 */
export const proposalDraftSuggestionSchema = z
  .object({
    orientacao: optionalText(500),
    confirmarSubstituicao: z.boolean().default(false),
  })
  .strict();

/** Finalizar a versão. A confirmação é explícita porque ela congela o documento. */
export const proposalDraftFinalizeSchema = z
  .object({
    /** Obrigatório a partir da segunda versão, como no registro documental. */
    motivo: optionalText(500),
    validadeIso: z.string().date().nullish(),
  })
  .strict();

/** Trazer para o rascunho a oportunidade como ela está hoje. */
export const proposalDraftRefreshSchema = z
  .object({
    confirmar: z.literal(true, {
      errorMap: () => ({ message: 'Confirme a atualização a partir da oportunidade.' }),
    }),
  })
  .strict();

// ===========================================================================
// CERES-041 — O BRIEFING DA PROPOSTA
// ===========================================================================

/**
 * Uma lista de frases do briefing (atividades, premissas, responsabilidades).
 *
 * Lista, e não um texto único com quebras: é ela que a proposta apresenta item
 * a item, e que a §6 deixa reordenar e remover. Um parágrafo corrido obrigaria
 * a tela a adivinhar onde cada promessa começa.
 */
const frasesDoBriefing = z.array(z.string().trim().min(1).max(600)).max(60);

/**
 * Um entregável do briefing.
 *
 * `origem` acompanha o conteúdo porque é ela que a tela mostra ("Padrão do
 * Playbook", "Adaptado nesta oportunidade") — e o servidor a recalcula na
 * escrita quando o texto copiado muda, para que a etiqueta nunca minta.
 */
export const opportunityBriefingDeliverableSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    descricao: optionalText(1000),
    origem: z.enum(BRIEFING_CONTENT_ORIGIN).default('OPORTUNIDADE'),
    /** A entrada do Playbook de onde veio. Rastro, e nunca vínculo vivo. */
    playbookEntryId: uuidSchema.nullish(),
  })
  .strict();

/** Uma etapa da metodologia, como ela vale NESTA negociação. */
export const opportunityBriefingStageSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    objetivo: optionalText(600),
    descricao: optionalText(1500),
    origem: z.enum(BRIEFING_CONTENT_ORIGIN).default('OPORTUNIDADE'),
    playbookEntryId: uuidSchema.nullish(),
  })
  .strict();

/**
 * Um marco do cronograma negociado.
 *
 * TEXTO no prazo, e não data: "30 dias após a assinatura" é o que se negocia, e
 * uma data no calendário obrigaria a inventar o dia da assinatura.
 */
export const opportunityBriefingMilestoneSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    prazo: optionalText(120),
    descricao: optionalText(600),
  })
  .strict();

/** Um perfil previsto para a execução. Sem valor: alocação não é preço. */
export const opportunityBriefingRoleSchema = z
  .object({
    perfil: z.string().trim().min(1).max(160),
    dedicacao: optionalText(120),
    descricao: optionalText(600),
  })
  .strict();

/** As informações gerais da negociação (§5.1). */
export const opportunityBriefingGeneralSchema = z
  .object({
    contextoDoCliente: optionalText(4000),
    problemaOuNecessidade: optionalText(4000),
    objetivoDaContratacao: optionalText(4000),
    resultadoEsperado: optionalText(4000),
    observacoes: optionalText(4000),
  })
  .strict();

/** O conteúdo específico de UM produto ou serviço negociado (§5.2). */
export const opportunityBriefingItemSchema = z
  .object({
    escopo: optionalText(6000),
    atividadesIncluidas: frasesDoBriefing.optional(),
    atividadesNaoIncluidas: frasesDoBriefing.optional(),
    entregaveis: z.array(opportunityBriefingDeliverableSchema).max(60).optional(),
    metodologia: z.array(opportunityBriefingStageSchema).max(40).optional(),
    premissas: frasesDoBriefing.optional(),
    responsabilidadesContratante: frasesDoBriefing.optional(),
    responsabilidadesContratada: frasesDoBriefing.optional(),
    cronograma: z.array(opportunityBriefingMilestoneSchema).max(40).optional(),
    equipe: z.array(opportunityBriefingRoleSchema).max(30).optional(),
    observacoes: optionalText(4000),
  })
  .strict();

/**
 * Copiar os padrões do Playbook para o briefing de um item.
 *
 * `substituir` é a confirmação de quem já tinha conteúdo ali: sem ela, a cópia
 * ACRESCENTA, e nunca apaga o que alguém escreveu.
 */
export const opportunityBriefingDefaultsSchema = z
  .object({
    entregaveis: z.boolean().default(true),
    metodologia: z.boolean().default(true),
    substituir: z.boolean().default(false),
  })
  .strict();

// ===========================================================================
// CERES-043 — O ENVIO DA PROPOSTA POR E-MAIL
// ===========================================================================

/**
 * Um endereço de destinatário.
 *
 * `\r` e `\n` são recusados ANTES de qualquer outra coisa: eles são o vetor
 * clássico de injeção de cabeçalho de e-mail, e um endereço com quebra de linha
 * transforma o campo "Para" em "Para + Bcc + o que o atacante quiser".
 */
const emailDeDestinatario = z
  .string()
  .trim()
  .min(3)
  .max(254)
  .refine((valor) => !/[\r\n]/.test(valor), 'Endereço de e-mail inválido.')
  .pipe(z.string().email('Endereço de e-mail inválido.'))
  .transform((valor) => valor.toLowerCase());

const listaDeDestinatarios = z.array(emailDeDestinatario).max(PROPOSAL_SEND_LIMITES.destinatarios);

/**
 * Enviar a proposta por e-mail.
 *
 * NENHUM campo de remetente: o endereço `From` é o que o provedor autoriza, e
 * aceitá-lo do cliente permitiria enviar em nome de qualquer domínio. O
 * `Reply-To` é uma ESCOLHA entre opções que o servidor oferece, e não um texto
 * livre — por isso é um enum, e não um endereço.
 */
export const proposalEmailSendSchema = z
  .object({
    /** A versão EXATA a enviar. Sem ela, vale a vigente — resolvida no servidor. */
    versaoId: uuidSchema.optional(),
    para: listaDeDestinatarios.min(1, 'Informe pelo menos um destinatário.'),
    copia: listaDeDestinatarios.optional(),
    copiaOculta: listaDeDestinatarios.optional(),
    /** `RESPONSAVEL` usa o e-mail de quem responde pela oportunidade. */
    responderPara: z.enum(['PROVEDOR', 'RESPONSAVEL']).default('PROVEDOR'),
    assunto: z
      .string()
      .trim()
      .min(3)
      .max(PROPOSAL_SEND_LIMITES.assunto)
      .refine((valor) => !/[\r\n]/.test(valor), 'O assunto não pode ter quebras de linha.'),
    mensagem: z.string().trim().min(10).max(PROPOSAL_SEND_LIMITES.corpo),
    /**
     * Confirmação de quem escolheu uma versão que não é a mais recente. Sem
     * ela, o servidor recusa — e nunca troca a versão escolhida por conta
     * própria.
     */
    confirmarVersaoAnterior: z.boolean().default(false),
    /**
     * CERES-044 — a confirmação de quem repete um envio de resultado INCERTO.
     *
     * Específica, e não a mesma de "confirmar envio": o risco aqui não é
     * enviar, é enviar DE NOVO o que talvez já tenha chegado ao cliente.
     */
    confirmarRiscoDeDuplicidade: z.boolean().default(false),
  })
  .strict();
